import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import ProductTagChip from "../../../../../../components/app-specific/product-tag-chip";
import {getProductTagsKey, normalizeProductTags} from "../../../../../../components/app-specific/product-tag-chip/normalize-product-tags";
import useTagLineOverflow from "./use-tag-line-overflow";
import debounce from "lodash/debounce";

const MAX_TAG_LINES = 2;

const injectStaticSvgClasses = (container) => {
    if (!container)
        return;
    const SVGs = container.querySelectorAll('svg');
    for (const svg of SVGs) {
        svg.parentElement?.parentElement?.parentElement?.parentElement?.classList.remove('static-svg');
        if (svg.classList.contains('static-svg')) {
            svg.parentElement?.parentElement?.parentElement?.parentElement?.classList.add('static-svg');
        }
    }
    const PNGs = container.querySelectorAll('img');
    for (const png of PNGs) {
        png.parentElement?.parentElement?.classList.remove('static-svg');
        png.parentElement?.parentElement?.classList.add('static-svg');
    }
};

const UserWebHomepageTagsContainer = ({tags, onTagClicked: onTagClickedProps}) => {
    const [state, setState] = useState({tags: []});
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<HTMLDivElement | null>}*/
    const containerRef = useRef(null);
    /**@type {React.MutableRefObject<HTMLDivElement | null>}*/
    const measureRef = useRef(null);

    const tagsKey = useMemo(() => getProductTagsKey(tags), [tags]);
    const normalizedTags = useMemo(() => normalizeProductTags(tags), [tagsKey]);

    const iconSize = useMemo(() => 14, []);
    const [remeasureKey, setRemeasureKey] = useState(0);

    const scheduleRemeasure = useMemo(
        () => debounce(() => setRemeasureKey((key) => key + 1), 150),
        [],
    );

    useLayoutEffect(() => {
        setState({tags: normalizedTags});
    }, [tagsKey, normalizedTags])

    useEffect(() => () => scheduleRemeasure.cancel(), [scheduleRemeasure]);

    const visibleCount = useTagLineOverflow({
        measureRef,
        tagCount: state.tags.length,
        maxLines: MAX_TAG_LINES,
        enabled: true,
        remeasureKey,
    });

    const visibleTags = useMemo(() => state.tags.slice(0, visibleCount), [state.tags, visibleCount]);
    const overflowCount = useMemo(() => Math.max(0, state.tags.length - visibleCount), [state.tags.length, visibleCount]);

    useEffect(() => {
        [containerRef.current, measureRef.current].forEach((container) => {
            if (!container) {
                return;
            }
            injectStaticSvgClasses(container);
        });
        const timeoutId = window.setTimeout(() => {
            [containerRef.current, measureRef.current].forEach((container) => {
                if (!container) {
                    return;
                }
                injectStaticSvgClasses(container);
            });
        }, 150);
        return () => window.clearTimeout(timeoutId);
    }, [state.tags]);

    const onTagReady = useCallback(() => {
        scheduleRemeasure();
    }, [scheduleRemeasure]);

    const onTagClicked = useCallback((e, tag) => {
        if (!isMounted() || !onTagClickedProps)
            return;
        e.preventDefault();
        e.stopPropagation();
        onTagClickedProps(tag);
    }, [isMounted, onTagClickedProps]);

    const renderTagChip = useCallback((tag, onReady) => (
        <ProductTagChip
            key={tag?.id}
            tag={tag}
            iconSize={iconSize}
            chipClassName={'user-web-homepage-tag'}
            onTagReady={onReady}
            onTagClicked={onTagClickedProps ? onTagClicked : undefined}
        />
    ), [iconSize, onTagClicked, onTagClickedProps]);

    if (!normalizedTags.length) {
        return null;
    }

    return (
        <div className={classnames(
            'user-web-homepage-product-tags-container',
            'user-web-homepage-product-tags-container--compact',
            'user-web-homepage-product-tags-container--line-clamp',
        )}>
            <div
                ref={measureRef}
                className={'tags-container tags-container--measure'}
                aria-hidden={true}
            >
                {state.tags.map((tag) => renderTagChip(tag, onTagReady))}
                <span
                    data-product-tag-overflow
                    className={'user-web-homepage-tag-overflow'}
                >
                    +0
                </span>
            </div>
            <div ref={containerRef} className={'tags-container'}>
                {visibleTags.map((tag) => renderTagChip(tag))}
                {
                    overflowCount > 0 &&
                    <span
                        data-product-tag-overflow
                        className={'user-web-homepage-tag-overflow'}
                        title={`${overflowCount} more tag${overflowCount > 1 ? 's' : ''}`}
                    >
                        +{overflowCount}
                    </span>
                }
            </div>
        </div>
    );
}

export default UserWebHomepageTagsContainer;
